import { Link } from '@tanstack/react-router';
import { useCalendly } from '@/lib/CalendlyContext';
import { useLang } from '@/lib/LanguageContext';
import { t } from '@/lib/translations';
import { ta } from '@/lib/i18n';
import { Modal, ModalContent, ModalTitle } from '@/components/ui/modal';

/**
 * CalendlyModal — controlled Radix dialog for booking flow.
 * - When `url` is empty (open question #10 — pending Océanne), renders a graceful
 *   fallback message + secondary CTA to #contact (closes modal on click).
 * - When `url` is provided, renders responsive Calendly iframe (vertical mobile,
 *   horizontal desktop). `loading="lazy"` for perf.
 *
 * State lives in CalendlyContext — any component can `useCalendly().open()`.
 * Mount once at root level (Task 5.4).
 */
export function CalendlyModal() {
  const { isOpen, url, close } = useCalendly();
  const { lang } = useLang();
  const text = ta(t.calendly, lang);

  return (
    <Modal open={isOpen} onOpenChange={(next) => { if (!next) close(); }}>
      <ModalContent className="max-w-lg md:max-w-2xl">
        <ModalTitle>{text.title}</ModalTitle>
        {url ? (
          <div className="aspect-[3/4] md:aspect-video w-full">
            <iframe
              src={url}
              className="w-full h-full border-0 rounded-md"
              title={text.title}
              loading="lazy"
            />
          </div>
        ) : (
          <div className="py-8 text-center space-y-4">
            <p className="text-base text-foreground/80">{text.fallback_message}</p>
            {/* Phase 27.33 — Codex audit #3 fix : Link cross-route (modal mount _root, peut s'ouvrir depuis n'importe quelle page) */}
            <Link
              to="/"
              hash="contact"
              onClick={close}
              className="inline-flex items-center justify-center px-5 py-2.5 bg-primary text-primary-foreground font-bold rounded-md text-sm uppercase tracking-wider hover:bg-primary/90 transition-colors"
            >
              {text.fallback_cta}
            </Link>
          </div>
        )}
      </ModalContent>
    </Modal>
  );
}
